/**
 * Syncs the height of numbered-list article rows to the height of the
 * OTHER sibling block(s) in the same .custom_block_wrap row (e.g. an
 * image block), rather than the wrap itself — since the wrap's height
 * is driven by its tallest child and would otherwise just reflect the
 * list's own (pre-sync) height back at itself.
 */
class ArticleListSync extends HTMLElement {
  connectedCallback() {
    this.blockWrap = this.closest('.custom_block_wrap');
    this.parentBlock = this.closest('[id^="block-"]');
    this.columnCount = parseInt(this.dataset.columns, 10) || 1;
    this.articleCards = Array.from(this.querySelectorAll('.article-card-list'));
    this.desktopMediaQuery = window.matchMedia('(min-width: 1024px)');
    this.siblingBlocks = this.getSiblingBlocks();

    this.handleResize = this.debounce(() => this.syncHeights(), 100);

    if (window.ResizeObserver && this.siblingBlocks.length) {
      this.resizeObserver = new ResizeObserver(() => this.syncHeights());
      this.siblingBlocks.forEach((sibling) => this.resizeObserver.observe(sibling));
    }

    window.addEventListener('resize', this.handleResize);
    this.desktopMediaQuery.addEventListener('change', this.handleResize);
    // Sibling images can finish loading (and change height) after first paint
    window.addEventListener('load', this.handleResize);

    this.syncHeights();
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    window.removeEventListener('resize', this.handleResize);
    this.desktopMediaQuery.removeEventListener('change', this.handleResize);
    window.removeEventListener('load', this.handleResize);
  }

  getSiblingBlocks() {
    if (!this.blockWrap || !this.parentBlock) return [];
    return Array.from(this.blockWrap.children).filter((child) => child !== this.parentBlock);
  }

  debounce(callback, waitMs) {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => callback.apply(this, args), waitMs);
    };
  }

  getTargetHeight() {
    if (!this.siblingBlocks.length) {
      // No other blocks to sync against — fall back to the wrap itself.
      return this.blockWrap ? this.blockWrap.offsetHeight : 0;
    }
    return Math.max(...this.siblingBlocks.map((sibling) => sibling.offsetHeight));
  }

  syncHeights() {
    const shouldSync = this.siblingBlocks.length > 0 && this.desktopMediaQuery.matches;

    if (!shouldSync) {
      this.articleCards.forEach((articleCard) => articleCard.style.removeProperty('height'));
      return;
    }

    const targetHeight = this.getTargetHeight();
    const rowCount = Math.ceil(this.articleCards.length / this.columnCount);

    if (!targetHeight || !rowCount) return;

    const rowGapPx = parseFloat(getComputedStyle(this).rowGap) || 0;
    const totalGapHeight = rowGapPx * (rowCount - 1);
    const rowHeightPx = (targetHeight - totalGapHeight) / rowCount;

    this.articleCards.forEach((articleCard) => {
      articleCard.style.height = `${rowHeightPx}px`;
    });
  }
}

customElements.define('article-list-sync', ArticleListSync);